import { useRef, useState } from 'react'
import { cn } from '@/lib/cn'
import { useExerciseLibrary } from '@/hooks/useExerciseLibrary'

/**
 * Searchable picker over the canonical exercise library. Selecting a movement
 * sets both the display name and the canonical `exercise_id` so logs group
 * correctly. Free-typing is still allowed (custom movement → slug id).
 */
export function ExercisePicker({
  value,
  exerciseId,
  onSelect,
  onFreeText,
}: {
  value: string
  exerciseId?: string | null
  onSelect: (id: string, name: string) => void
  onFreeText: (name: string) => void
}) {
  const { search, exercises, loading } = useExerciseLibrary()
  const [open, setOpen] = useState(false)
  const [query, setQuery] = useState('')
  const blurTimer = useRef<number>()

  const results = open ? search(query || value, 8) : []
  const matched = Boolean(exerciseId && exercises.some((e) => e.id === exerciseId))

  return (
    <div className="relative flex-1">
      <div className="relative">
        <input
          value={value}
          onChange={(e) => {
            setQuery(e.target.value)
            onFreeText(e.target.value)
            setOpen(true)
          }}
          onFocus={() => setOpen(true)}
          onBlur={() => {
            blurTimer.current = window.setTimeout(() => setOpen(false), 120)
          }}
          placeholder={loading ? 'Loading library…' : 'Search exercise…'}
          className={cn(
            'w-full rounded-lg border bg-white/[.04] px-2.5 pr-7 h-10 font-condensed text-base font-bold text-text outline-none focus:border-gold focus:ring-2 focus:ring-gold/20 placeholder:text-subtle',
            matched ? 'border-success/40' : 'border-border-strong'
          )}
        />
        {matched && (
          <span
            className="absolute right-2 top-1/2 -translate-y-1/2 text-success"
            title="Matched to your exercise library"
          >
            ✓
          </span>
        )}
      </div>

      {open && results.length > 0 && (
        <div className="absolute z-30 mt-1 max-h-64 w-full overflow-y-auto rounded-lg border border-border-strong bg-surface-2 shadow-card-hover">
          {results.map((e) => (
            <button
              key={e.id}
              type="button"
              onMouseDown={(ev) => {
                // onMouseDown fires before blur so the selection isn't lost
                ev.preventDefault()
                window.clearTimeout(blurTimer.current)
                onSelect(e.id, e.name)
                setQuery('')
                setOpen(false)
              }}
              className="flex w-full items-center justify-between gap-2 border-b border-border px-3 py-2 text-left last:border-0 hover:bg-surface-3"
            >
              <span className="min-w-0">
                <span className="block truncate font-condensed text-sm font-bold">{e.name}</span>
                {e.category && (
                  <span className="block truncate text-[11px] text-muted">{e.category}</span>
                )}
              </span>
              {e.is_plyometric && (
                <span className="flex-shrink-0 rounded-full border border-data/30 bg-data/10 px-1.5 py-0.5 text-[9px] uppercase tracking-wide text-data">
                  plyo
                </span>
              )}
            </button>
          ))}
        </div>
      )}
    </div>
  )
}
